import { useEffect } from 'react';
import { Modal, Stack, TextInput, Textarea, Select, NumberInput, Checkbox, Button, Group } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useForm, zodResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { maintenanceSchema, type MaintenanceFormData } from '../schemas/maintenanceSchema';
import { useCreateMaintenance } from '@/hooks/api/useMaintenance';
import { usePeople } from '@/hooks/api/usePeople';
import { useOrganization } from '@/hooks/useOrganization';

interface MaintenanceFormProps {
  opened: boolean;
  onClose: () => void;
  assetId: string;
}

const statusOptions = [
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'overdue', label: 'Overdue' },
];

const frequencyOptions = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi-annually', label: 'Semi-Annually' },
  { value: 'annually', label: 'Annually' },
];

export function MaintenanceForm({ opened, onClose, assetId }: MaintenanceFormProps) {
  const { organizationId } = useOrganization();
  const createMaintenance = useCreateMaintenance();
  const { data: people = [] } = usePeople(organizationId);

  const form = useForm<MaintenanceFormData>({
    validate: zodResolver(maintenanceSchema),
    initialValues: {
      title: '',
      details: '',
      date_completed: new Date().toISOString(),
      status: 'completed',
      cost: 0,
      repeating: false,
      frequency: '',
      next_maintenance_date: '',
      maintenance_by_id: null,
    },
  });

  // Reset form when modal opens
  useEffect(() => {
    if (opened) {
      form.reset();
    }
  }, [opened]);

  const handleSubmit = async (values: MaintenanceFormData) => {
    try {
      await createMaintenance.mutateAsync({
        assetId,
        organizationId,
        data: values,
      });

      notifications.show({
        title: 'Success',
        message: 'Maintenance record created successfully',
        color: 'green',
      });

      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create maintenance record',
        color: 'red',
      });
    }
  };

  const peopleOptions = people.map((person) => ({
    value: person.id,
    label: person.full_name,
  }));

  return (
    <Modal opened={opened} onClose={onClose} title="Add Maintenance Record" size="md">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Title"
            placeholder="e.g., Annual Inspection"
            required
            {...form.getInputProps('title')}
          />

          <Textarea
            label="Details"
            placeholder="Describe the maintenance work performed"
            required
            minRows={3}
            {...form.getInputProps('details')}
          />

          <DateTimePicker
            label="Date Completed"
            placeholder="Select date and time"
            required
            value={form.values.date_completed ? new Date(form.values.date_completed) : null}
            onChange={(value) => form.setFieldValue('date_completed', value?.toISOString() || '')}
            error={form.errors.date_completed}
          />

          <Select
            label="Status"
            placeholder="Select status"
            required
            data={statusOptions}
            {...form.getInputProps('status')}
          />

          <NumberInput
            label="Cost"
            placeholder="0.00"
            prefix="$"
            decimalScale={2}
            min={0}
            {...form.getInputProps('cost')}
          />

          <Select
            label="Performed By"
            placeholder="Select person (optional)"
            data={peopleOptions}
            searchable
            clearable
            {...form.getInputProps('maintenance_by_id')}
          />

          <Checkbox
            label="Recurring Maintenance"
            {...form.getInputProps('repeating', { type: 'checkbox' })}
          />

          {form.values.repeating && (
            <>
              <Select
                label="Frequency"
                placeholder="Select frequency"
                data={frequencyOptions}
                {...form.getInputProps('frequency')}
              />

              <DateTimePicker
                label="Next Maintenance Date"
                placeholder="Select date and time"
                value={form.values.next_maintenance_date ? new Date(form.values.next_maintenance_date) : null}
                onChange={(value) => form.setFieldValue('next_maintenance_date', value?.toISOString() || '')}
                error={form.errors.next_maintenance_date}
              />
            </>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={createMaintenance.isPending}>
              Add Maintenance Record
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
